'use client'

import React, { useCallback, useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Anchor, Button, Checkbox, Flex, LoadingOverlay, Notification, Space, Text, TextInput } from '@mantine/core'
import { useForm } from 'react-hook-form'
import { IMaskInput } from 'react-imask'
import { useCookie } from '@phoenix-web/react-hooks'
import { Audience } from '@phoenix-web/utils/journey'
import { emailRegEx, INVALID_CHARS_FOR_NAME } from '@phoenix-web/utils/validators'
import {
  COURTYARD2_AUTH_SIGNUP_STEP2_PATH,
  DO_NOT_SELL_INFO_URL,
  KNOCK_RETURN_TO_COOKIE,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from '@/constants'
import { LeadInfoBody, useSaveLeadInfoToCache } from './useAccountSignup'
import styles from './AccountSignUpForm.module.css'

type AccountSignupFormValues = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  sms: boolean
  audience?: Audience
}

const validateName = (value: string) => {
  const invalidChars = INVALID_CHARS_FOR_NAME.filter((char) => value.includes(char))
  return !!invalidChars.length ? `Invalid special characters: ${invalidChars.join(' ')}` : true
}

export default function AccountSignupForm() {
  // Initialize the state for the component
  const [showBackButton, setShowBackButton] = useState(false)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false)
  const [showErrorNotification, setShowErrorNotification] = useState(false)

  const [saveLeadInfoToCache] = useSaveLeadInfoToCache()
  const router = useRouter()
  const searchParams = useSearchParams()

  // Get the search params values
  const invitationToken = searchParams?.get('invitation_token') || searchParams?.get('invitation_uuid')
  // Carried in from the lead-gen email link (?lead_id=). Stored alongside the cached lead
  // info so the Auth0 create-user script can hand it to create-account, which connects the
  // new account to its existing lead and transaction.
  const leadId = searchParams?.get('lead_id')
  const returnTo = searchParams?.get('returnTo')
  const returnToSearchQuery = returnTo ? `returnTo=${encodeURIComponent(returnTo)}` : ''
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setShowBackButton((window as any).history?.length > 2)
    }
  }, [])

  const [_returnToCookie, setReturnToCookie] = useCookie(KNOCK_RETURN_TO_COOKIE, undefined)

  const {
    register,
    handleSubmit,
    clearErrors,
    setError,
    setValue,
    formState: { errors },
  } = useForm<AccountSignupFormValues>()

  const onSubmit = useCallback(
    async (data: AccountSignupFormValues, _e?: React.BaseSyntheticEvent) => {
      clearErrors()
      setShowLoadingOverlay(true)
      const payload: LeadInfoBody = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone_number: data.phoneNumber,
        notification_preference: data.sms ? 'sms' : 'email',
        audience: 'customer', // phoenix-ux-gql is expecting 'customer' as its homebuyer audience
        // By clicking "Continue", user agrees to Knock's terms of use and privacy policy
        terms_of_service: true,
        privacy_policy_consent: true,
      }
      if (invitationToken) {
        payload.invitation_token = invitationToken as string
      }
      if (leadId) {
        payload.lead_id = leadId
      }
      const returnTo = searchParams?.get('returnTo')
      if (returnTo) {
        setReturnToCookie(returnTo, {})
      }

      saveLeadInfoToCache({ variables: { input: payload } })
        .then((result) => {
          if (result.data?.saveLeadInfoToCache?.success) {
            // Redirect the user to Auth0 Signup with email and return redirect url
            if (typeof window !== 'undefined') {
              window.location.href = `${COURTYARD2_AUTH_SIGNUP_STEP2_PATH}?email=${encodeURIComponent(
                data.email
              )}&${returnToSearchQuery}`
            }
          } else {
            setShowLoadingOverlay(false)
            setShowErrorNotification(true)
          }
        })
        .catch((e) => {
          // Update the phone number error to be the specific error returned from backend
          if (e && e.graphQLErrors && e.graphQLErrors[0]) {
            const { message = '', errorType = '' } = e.graphQLErrors[0]?.extensions?.errorDetail
            if (
              [
                'EmptyPhoneNumberError',
                'ValidatePhoneNumberInternalError',
                'InvalidUSPhoneNumberError',
                'InvalidUSMobilePhoneNumberError',
                'ValidatePhoneNumberError',
              ].includes(errorType)
            ) {
              setError('phoneNumber', {
                type: 'custom',
                message,
              })
            } else if (errorType === 'VerifyEmailError') {
              setError('email', {
                type: 'custom',
                message,
              })
            }
          }
          setShowLoadingOverlay(false)
        })
    },
    [
      clearErrors,
      setShowLoadingOverlay,
      saveLeadInfoToCache,
      returnToSearchQuery,
      setError,
      invitationToken,
      leadId,
      setReturnToCookie,
      searchParams,
    ]
  )
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} style={{ position: 'relative' }}>
        <LoadingOverlay visible={showLoadingOverlay} zIndex={99} />
        <TextInput
          {...register('firstName', {
            required: 'First name is required',
            validate: validateName,
          })}
          id="firstName"
          error={errors.firstName?.message}
          placeholder="First name"
          size="lg"
          classNames={{ input: styles.textInput, error: styles.inputError }}
          data-heap-redact-text
        />
        <Space h="lg" />
        <TextInput
          {...register('lastName', {
            required: 'Last name is required',
            validate: validateName,
          })}
          id="lastName"
          error={errors.lastName?.message}
          placeholder="Last name"
          size="lg"
          classNames={{ input: styles.textInput, error: styles.inputError }}
          data-heap-redact-text
        />
        <Space h="lg" />
        <TextInput
          {...register('email', {
            required: 'Email address is required',
            pattern: {
              value: emailRegEx,
              message: 'Email is not valid',
            },
          })}
          id="email"
          error={errors.email?.message}
          placeholder="Email address"
          size="lg"
          classNames={{ input: styles.textInput, error: styles.inputError }}
          data-heap-redact-text
        />
        <Space h="lg" />
        <TextInput
          {...register('phoneNumber', {
            required: 'Phone number is required',
          })}
          id="phoneNumber"
          error={errors.phoneNumber?.message}
          placeholder="Phone number"
          size="lg"
          classNames={{ input: styles.textInput, error: styles.inputError }}
          data-heap-redact-text
          component={IMaskInput}
          // @ts-expect-error type issue due to IMaskInput
          mask="+1 (000) 000-0000"
          onAccept={(value: string) => {
            setValue('phoneNumber', value)
          }}
        />
        <Space h="lg" />
        <Checkbox
          id="sms"
          {...register('sms')}
          label={
            <Text size="sm">
              Yes, send me text messages from Knock about my account (updates, reminders, and account alerts). Msg
              frequency varies. Msg &amp; data rates may apply. Reply HELP for help, STOP to opt out. See our{' '}
              <Anchor href={PRIVACY_POLICY_URL} target="_blank" fw={700}>
                privacy policy
              </Anchor>
              .
            </Text>
          }
          radius="xs"
        />
        <Space h="lg" />
        <Text size="sm">
          By clicking &ldquo;Continue&rdquo;, you agree to{' '}
          <Anchor href={TERMS_OF_USE_URL} target="_blank" fw={700}>
            Knock&rsquo;s terms of use
          </Anchor>{' '}
          and{' '}
          <Anchor href={PRIVACY_POLICY_URL} target="_blank" fw={700}>
            privacy policy
          </Anchor>
          , which includes the{' '}
          <Anchor href={DO_NOT_SELL_INFO_URL} target="_blank" fw={700}>
            consumer privacy act
          </Anchor>
          .
        </Text>
        <Space h="md" />
        <Space h="md" />
        {showErrorNotification && (
          <>
            <Notification
              classNames={{ title: styles.notificationTitle, description: styles.notificationDescription }}
              onClose={() => setShowErrorNotification(false)}
              title="Error. Please try again."
              color="red"
            >
              If the problem persists, please contact Knock support.
            </Notification>
            <Space h="lg" />
          </>
        )}
        <Flex direction="column" gap="sm">
          <Button radius="sm" type="submit" style={{ height: 52 }}>
            Continue
          </Button>
          {showBackButton ? (
            <Button radius="sm" variant="default" color="dark" onClick={() => router.push('/')} style={{ height: 52 }}>
              Cancel
            </Button>
          ) : null}
        </Flex>
      </form>
    </>
  )
}
